import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Briefcase,
  Copy,
  ExternalLink,
  Linkedin,
  Loader2,
  Save,
  Send,
  Trash2,
} from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { LoginGate } from "@/components/LoginGate";
import {
  SearchForm,
  fromSearch,
  toSearchUpdatePayload,
  type SearchFormValues,
} from "@/components/clients/SearchForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StatusBanner } from "@/components/ui/status-banner";
import { buildApplyUrl, buildLinkedInShareUrl } from "@/lib/linkedin";
import { supabase } from "@/lib/supabase";
import {
  EXPERIENCE_LABELS,
  type Company,
  type Search as JobSearch,
} from "@/lib/types";

export const Route = createFileRoute("/searches/$searchId")({
  component: SearchDetailPage,
});

function SearchDetailPage() {
  const { searchId } = Route.useParams();
  return (
    <LoginGate title="DeepDive" subtitle="Iniciá sesión para ver la búsqueda.">
      {({ user, signOut }) => (
        <AppShell user={user} onLogout={signOut}>
          <SearchDetail searchId={searchId} />
        </AppShell>
      )}
    </LoginGate>
  );
}

function SearchDetail({ searchId }: { searchId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState<SearchFormValues | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [feedback, setFeedback] = useState<{
    kind: "success" | "error";
    title: string;
    detail?: string;
  } | null>(null);

  const { data: search, isLoading } = useQuery({
    queryKey: ["search", searchId],
    queryFn: async (): Promise<JobSearch> => {
      const { data, error } = await supabase
        .from("searches")
        .select("*")
        .eq("id", searchId)
        .single();
      if (error) throw error;
      return data as JobSearch;
    },
  });

  const { data: company } = useQuery({
    queryKey: ["company", search?.company_id],
    queryFn: async (): Promise<Company | null> => {
      if (!search?.company_id) return null;
      const { data, error } = await supabase
        .from("companies")
        .select("*")
        .eq("id", search.company_id)
        .single();
      if (error) throw error;
      return data as Company;
    },
    enabled: !!search?.company_id,
  });

  useEffect(() => {
    if (search && !form) {
      setForm(fromSearch(search));
    }
  }, [search, form]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!form) return;
      if (!form.puesto.trim()) throw new Error("Puesto requerido.");
      const { error } = await supabase
        .from("searches")
        .update(toSearchUpdatePayload(form))
        .eq("id", searchId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["search", searchId] });
      qc.invalidateQueries({ queryKey: ["searches"] });
      setFeedback({ kind: "success", title: "Cambios guardados" });
    },
    onError: (e: Error) =>
      setFeedback({ kind: "error", title: "Error", detail: e.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("searches")
        .delete()
        .eq("id", searchId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["searches"] });
      if (search?.company_id) {
        navigate({
          to: "/clients/$clientId",
          params: { clientId: search.company_id },
        });
      } else {
        navigate({ to: "/clients" });
      }
    },
    onError: (e: Error) =>
      setFeedback({
        kind: "error",
        title: "No se pudo borrar",
        detail: e.message,
      }),
  });

  if (isLoading || !search || !form) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  function shareLinkedIn() {
    if (!search || !company) {
      setFeedback({
        kind: "error",
        title: "Faltan datos del cliente para armar el post",
      });
      return;
    }
    const applyUrl = buildApplyUrl(window.location.origin, search.id);
    const url = buildLinkedInShareUrl(search, company, applyUrl);
    window.open(url, "_blank", "noopener,noreferrer");
  }

  async function copyApplyLink() {
    if (!search) return;
    const applyUrl = buildApplyUrl(window.location.origin, search.id);
    await navigator.clipboard.writeText(applyUrl);
    setFeedback({
      kind: "success",
      title: "Link de postulación copiado",
      detail: applyUrl,
    });
  }

  function sendTest() {
    if (!search || !company) return;
    navigate({
      to: "/generate",
      search: {
        company_id: company.id,
        search_id: search.id,
      },
    });
  }

  return (
    <div className="px-6 py-5">
      {company && (
        <Link
          to="/clients/$clientId"
          params={{ clientId: company.id }}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-3 w-3" />
          {company.name}
        </Link>
      )}

      <div className="flex items-start justify-between gap-4 mb-8">
        <div className="flex items-center gap-3 min-w-0">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-primary/10 border border-primary/30 shrink-0">
            <Briefcase className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight truncate">
                {search.puesto}
              </h1>
              <Badge variant="muted" className="normal-case tracking-normal">
                {EXPERIENCE_LABELS[search.experience]}
              </Badge>
              {!search.is_open && (
                <Badge variant="outline">Cerrada</Badge>
              )}
            </div>
            {company && (
              <p className="text-muted-foreground text-sm mt-0.5">
                {company.name}
                {search.rubro && ` · ${search.rubro}`}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="default" size="sm" onClick={copyApplyLink}>
            <Copy className="h-3.5 w-3.5" />
            Copiar link de postulación
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={shareLinkedIn}
            disabled={!company}
          >
            <Linkedin className="h-3.5 w-3.5" />
            Publicar en LinkedIn
            <ExternalLink className="h-3 w-3 opacity-50" />
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={sendTest}
            disabled={!company || !search.is_open}
          >
            <Send className="h-3.5 w-3.5" />
            Enviar una prueba
          </Button>
        </div>
      </div>

      {feedback && (
        <div className="mb-6">
          <StatusBanner
            variant={feedback.kind}
            title={feedback.title}
            onDismiss={() => setFeedback(null)}
          >
            {feedback.detail}
          </StatusBanner>
        </div>
      )}

      {!search.is_open && (
        <div className="mb-6">
          <StatusBanner variant="info" title="Búsqueda cerrada">
            No podés enviar pruebas nuevas. Marcala como abierta abajo si querés
            reactivarla.
          </StatusBanner>
        </div>
      )}

      <Card className="p-6 mb-6">
        <SearchForm value={form} onChange={setForm} />
        <div className="flex justify-between items-center mt-6 pt-5 border-t border-border">
          <Button
            variant="outlineDanger"
            size="sm"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Borrar
          </Button>
          <Button
            variant="primary"
            onClick={() => saveMutation.mutate()}
            loading={saveMutation.isPending}
            disabled={!form.puesto.trim()}
          >
            <Save className="h-4 w-4" />
            Guardar cambios
          </Button>
        </div>
      </Card>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Borrar búsqueda</DialogTitle>
            <DialogDescription>
              Se va a borrar <strong>{search.puesto}</strong>. Las invitaciones
              vinculadas quedan con la referencia nula (no se borran).
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="default" onClick={() => setDeleteOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              loading={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
              Borrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
